import { MAX_PLAYERS } from '@ogm/contracts/input';
const BUTTONS = {
    a: 0,
    b: 1,
    x: 2,
    y: 3,
    lb: 4,
    rb: 5,
    lt: 6,
    rt: 7,
    select: 8,
    start: 9,
};
export function defaultGamepadMapping(actions) {
    const mapping = {};
    const sticks = ['leftStick', 'rightStick'];
    const buttons = ['a', 'x', 'b', 'y', 'lb', 'rb'];
    let stick = 0;
    let button = 0;
    for (const [action, kind] of Object.entries(actions)) {
        if (kind === 'stick')
            mapping[action] = sticks[Math.min(stick++, sticks.length - 1)];
        else if (kind === 'button')
            mapping[action] = buttons[Math.min(button++, buttons.length - 1)];
    }
    return mapping;
}
export class GamepadPoller {
    options;
    lastInputAt = -Infinity;
    frameCallbacks = new Set();
    snapshotCallbacks = new Set();
    claims = new Map();
    previous = new Map();
    getGamepads;
    raf;
    cancelRaf;
    now;
    mapping = {};
    actions = {};
    mode = 'play';
    rafHandle;
    constructor(options = {}) {
        this.options = options;
        this.getGamepads = options.getGamepads ?? (() => navigator.getGamepads?.() ?? []);
        this.raf = options.raf ?? window.requestAnimationFrame.bind(window);
        this.cancelRaf = options.cancelRaf ?? window.cancelAnimationFrame.bind(window);
        this.now = options.now ?? (() => performance.now());
    }
    setMapping(mapping, actions) {
        this.actions = actions;
        this.mapping = { ...defaultGamepadMapping(actions), ...mapping };
    }
    setMode(mode) {
        this.mode = mode;
    }
    onFrame(callback) {
        this.frameCallbacks.add(callback);
        return () => this.frameCallbacks.delete(callback);
    }
    onPadSnapshot(callback) {
        this.snapshotCallbacks.add(callback);
        return () => this.snapshotCallbacks.delete(callback);
    }
    start() {
        if (this.rafHandle !== undefined)
            return;
        const loop = () => {
            this.poll();
            this.rafHandle = this.raf(loop);
        };
        this.rafHandle = this.raf(loop);
    }
    stop() {
        if (this.rafHandle === undefined)
            return;
        this.cancelRaf(this.rafHandle);
        this.rafHandle = undefined;
    }
    poll(at = this.now()) {
        const pads = Array.from(this.getGamepads());
        const connected = new Set(pads.filter((pad) => pad?.connected).map((pad) => pad.index));
        for (const [deviceIndex, playerIndex] of this.claims) {
            if (connected.has(deviceIndex))
                continue;
            const previous = this.previous.get(playerIndex);
            if (this.mode === 'play' && previous) {
                const actions = this.neutralActions(previous.actions);
                if (!this.sameActions(previous.actions, actions)) {
                    const frame = { playerIndex, tick: Math.max(0, Math.floor(at)), source: 'gamepad', actions };
                    for (const callback of this.frameCallbacks)
                        callback(frame);
                }
            }
            this.claims.delete(deviceIndex);
            this.previous.delete(playerIndex);
        }
        for (const pad of pads) {
            if (!pad?.connected)
                continue;
            if (!this.claims.has(pad.index) && this.anyButtonPressed(pad))
                this.claim(pad.index);
            const playerIndex = this.claims.get(pad.index);
            if (playerIndex === undefined)
                continue;
            const snapshot = this.snapshot(pad, playerIndex);
            if (this.anyButtonPressed(pad) || pad.axes.some((value) => Math.abs(value) > 0.15)) {
                this.lastInputAt = at;
                this.options.onActivity?.(at);
            }
            if (this.mode === 'edit') {
                for (const callback of this.snapshotCallbacks)
                    callback(snapshot);
                continue;
            }
            const actions = this.mapActions(pad);
            const previous = this.previous.get(playerIndex);
            if (previous && this.sameActions(previous.actions, actions) && at - previous.sentAt < 250)
                continue;
            this.previous.set(playerIndex, { actions, sentAt: at });
            const frame = { playerIndex, tick: Math.max(0, Math.floor(at)), source: 'gamepad', actions };
            for (const callback of this.frameCallbacks)
                callback(frame);
        }
    }
    claim(deviceIndex) {
        const used = new Set(this.claims.values());
        for (let playerIndex = 0; playerIndex < MAX_PLAYERS; playerIndex += 1) {
            if (!used.has(playerIndex)) {
                this.claims.set(deviceIndex, playerIndex);
                return;
            }
        }
    }
    mapActions(pad) {
        const values = {};
        for (const [action, kind] of Object.entries(this.actions)) {
            const binding = this.mapping[action];
            if (!binding)
                continue;
            values[action] = this.readBinding(pad, binding, kind);
        }
        return values;
    }
    readBinding(pad, binding, kind) {
        if (binding === 'leftStick' || binding === 'rightStick') {
            const offset = binding === 'leftStick' ? 0 : 2;
            const x = this.deadZone(pad.axes[offset] ?? 0);
            const y = this.deadZone(pad.axes[offset + 1] ?? 0);
            return kind === 'axis' ? x : { x, y };
        }
        if (binding === 'dpad') {
            return {
                x: Number(pad.buttons[15]?.pressed ?? false) - Number(pad.buttons[14]?.pressed ?? false),
                y: Number(pad.buttons[13]?.pressed ?? false) - Number(pad.buttons[12]?.pressed ?? false),
            };
        }
        const button = pad.buttons[BUTTONS[binding] ?? -1];
        return kind === 'axis' ? (button?.value ?? 0) : (button?.pressed ?? false);
    }
    snapshot(pad, playerIndex) {
        return {
            playerIndex,
            deviceIndex: pad.index,
            buttons: pad.buttons.map((button) => button.value),
            axes: [...pad.axes],
            timestamp: pad.timestamp,
        };
    }
    anyButtonPressed(pad) {
        return pad.buttons.some((button) => button.pressed || button.value > 0.5);
    }
    deadZone(value) {
        return Math.abs(value) < 0.15 ? 0 : value;
    }
    sameActions(left, right) {
        return JSON.stringify(left) === JSON.stringify(right);
    }
    neutralActions(actions) {
        return Object.fromEntries(Object.entries(actions).map(([action, value]) => [
            action,
            typeof value === 'boolean' ? false : typeof value === 'number' ? 0 : { x: 0, y: 0 },
        ]));
    }
}
//# sourceMappingURL=gamepad.js.map